import React from "react";
var __jsx = React.createElement;
import { useRef, useState, useCallback } from "react";
import { twMerge } from "tailwind-merge";
var Slider = function Slider(_ref) {
  var _marks$0$value, _marks$, _marks$value, _marks;
  var value = _ref.value,
    onChange = _ref.onChange,
    marks = _ref.marks,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    reverse = _ref.reverse,
    _ref$paddedEdge = _ref.paddedEdge,
    paddedEdge = _ref$paddedEdge === void 0 ? false : _ref$paddedEdge,
    _ref$paddedEdgePercen = _ref.paddedEdgePercent,
    paddedEdgePercent = _ref$paddedEdgePercen === void 0 ? 8 : _ref$paddedEdgePercen,
    className = _ref.className;
  var trackRef = useRef(null);
  var _useState = useState(false),
    isActive = _useState[0],
    setIsActive = _useState[1];
  var _useState2 = useState(false),
    isFocused = _useState2[0],
    setIsFocused = _useState2[1];
  // Raw visual left % (0–100) while dragging — follows pointer before snapping.
  var _useState3 = useState(null),
    rawLeft = _useState3[0],
    setRawLeft = _useState3[1];
  var min = (_marks$0$value = (_marks$ = marks[0]) === null || _marks$ === void 0 ? void 0 : _marks$.value) !== null && _marks$0$value !== void 0 ? _marks$0$value : 0;
  var max = (_marks$value = (_marks = marks[marks.length - 1]) === null || _marks === void 0 ? void 0 : _marks.value) !== null && _marks$value !== void 0 ? _marks$value : 100;
  var range = max - min || 1;

  // When `reverse` is explicitly provided, use it; otherwise read direction from the body element.
  var fillFromRight = reverse !== undefined ? reverse : typeof document !== "undefined" ? document.body.dir === "rtl" : true;

  // Edge inset: marks sit at edgePad%/(100-edgePad)% instead of 0%/100%.
  var edgePad = paddedEdge ? paddedEdgePercent !== null && paddedEdgePercent !== void 0 ? paddedEdgePercent : 8 : 0;
  var snapToNearest = useCallback(function (rawValue) {
    return marks.reduce(function (prev, curr) {
      return Math.abs(curr.value - rawValue) < Math.abs(prev.value - rawValue) ? curr : prev;
    }).value;
  }, [marks]);
  var getValueFromPointer = useCallback(function (clientX) {
    var _trackRef$current;
    var rect = (_trackRef$current = trackRef.current) === null || _trackRef$current === void 0 ? void 0 : _trackRef$current.getBoundingClientRect();
    if (!rect) return value;
    var pct = (clientX - rect.left) / rect.width;
    if (fillFromRight) pct = 1 - pct;
    pct = Math.max(0, Math.min(1, pct));
    if (edgePad > 0) {
      var edgePadFrac = edgePad / 100;
      pct = (pct - edgePadFrac) / (1 - 2 * edgePadFrac);
      pct = Math.max(0, Math.min(1, pct));
    }
    return snapToNearest(min + pct * range);
  }, [edgePad, fillFromRight, min, range, snapToNearest, value]);

  // Raw visual left % clamped to the valid mark range [edgePad, 100-edgePad].
  var getRawLeftFromPointer = function getRawLeftFromPointer(clientX) {
    var _trackRef$current2;
    var rect = (_trackRef$current2 = trackRef.current) === null || _trackRef$current2 === void 0 ? void 0 : _trackRef$current2.getBoundingClientRect();
    if (!rect) return thumbLeft;
    var raw = (clientX - rect.left) / rect.width * 100;
    return Math.max(edgePad, Math.min(100 - edgePad, raw));
  };
  var handlePointerDown = function handlePointerDown(e) {
    if (disabled) return;
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setIsActive(true);
    setRawLeft(getRawLeftFromPointer(e.clientX));
    onChange(getValueFromPointer(e.clientX));
  };
  var handlePointerMove = function handlePointerMove(e) {
    if (!isActive || disabled) return;
    setRawLeft(getRawLeftFromPointer(e.clientX));
    onChange(getValueFromPointer(e.clientX));
  };
  var handlePointerUp = function handlePointerUp() {
    setIsActive(false);
    setRawLeft(null);
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (disabled) return;
    var idx = marks.findIndex(function (m) {
      return m.value === value;
    });
    if (e.key === "ArrowRight" || e.key === "ArrowUp") {
      e.preventDefault();
      var next = marks[idx + 1];
      if (next) onChange(next.value);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
      e.preventDefault();
      var prev = marks[idx - 1];
      if (prev) onChange(prev.value);
    } else if (e.key === "Home") {
      e.preventDefault();
      onChange(marks[0].value);
    } else if (e.key === "End") {
      e.preventDefault();
      onChange(marks[marks.length - 1].value);
    }
  };

  // Maps a value to a visual percentage, accounting for optional edge padding.
  var getPct = function getPct(val) {
    var normalized = (val - min) / range * 100;
    return edgePad + normalized / 100 * (100 - 2 * edgePad);
  };
  var thumbPct = getPct(value);
  // Visual left % of the thumb (0 = leftmost, 100 = rightmost)
  var thumbLeft = fillFromRight ? 100 - thumbPct : thumbPct;

  // During drag: follow raw pointer. At rest: use snapped value position.
  var displayLeft = isActive && rawLeft !== null ? rawLeft : thumbLeft;
  var displayFillWidth = isActive && rawLeft !== null ? fillFromRight ? 100 - rawLeft : rawLeft : thumbPct;
  var thumbShadow = function () {
    if (disabled) return "";
    if (isActive) return "shadow-[0_0_0_4px_rgba(13,13,181,0.16)]";
    if (isFocused) return "shadow-[0_0_0_6px_rgba(13,13,181,0.10)]";
    return "shadow-[0_2px_3px_rgba(13,13,181,0.35)]";
  }();
  return /*#__PURE__*/React.createElement("div", {
    role: "slider",
    "aria-valuemin": min,
    "aria-valuemax": max,
    "aria-valuenow": value,
    "aria-disabled": disabled,
    tabIndex: disabled ? -1 : 0
    // Force physical LTR when reverse is set so Arabic pages don't re-flip
    ,
    dir: reverse !== undefined ? "ltr" : undefined,
    onKeyDown: handleKeyDown,
    onFocus: function onFocus() {
      return !disabled && setIsFocused(true);
    },
    onBlur: function onBlur() {
      setIsFocused(false);
      setIsActive(false);
    },
    className: twMerge("relative select-none outline-none touch-none",
    // px-2.5 = half thumb width so thumb at 0% / 100% doesn't overflow container
    "px-2.5 pt-8 pb-7", disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer", className)
  }, /*#__PURE__*/React.createElement("div", {
    ref: trackRef,
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerUp,
    className: "relative",
    style: {
      height: 50
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "absolute left-0 right-0 h-1 top-1/2 -translate-y-1/2 rounded-full bg-gray-gainsboro"
  }), /*#__PURE__*/React.createElement("div", {
    className: twMerge("absolute h-1 top-1/2 -translate-y-1/2 rounded-full", disabled ? "bg-gray-400" : "bg-primary-new", !isActive && "transition-[width] duration-300 ease-in-out"),
    style: fillFromRight ? {
      right: 0,
      width: "".concat(displayFillWidth, "%")
    } : {
      left: 0,
      width: "".concat(displayFillWidth, "%")
    }
  }), marks.map(function (mark, idx) {
    var pct = getPct(mark.value);
    var dotLeft = fillFromRight ? 100 - pct : pct;
    var isPassed = fillFromRight ? mark.value >= value : mark.value <= value;
    var isSelected = mark.value === value;
    var isFirst = idx === 0;
    var isLast = idx === marks.length - 1;
    return /*#__PURE__*/React.createElement("div", {
      key: mark.value,
      className: "absolute top-1/2",
      style: {
        left: "".concat(dotLeft, "%"),
        transform: "translate(-50%, -50%)"
      }
    }, /*#__PURE__*/React.createElement("div", {
      className: twMerge("w-2 h-2 rounded-full border-2 bg-white transition-colors duration-150", isSelected && !isActive ? "opacity-0" : isPassed ? disabled ? "border-gray-400" : "border-primary-new" : "border-gray-gainsboro")
    }), mark.currentLabel && /*#__PURE__*/React.createElement("div", {
      className: "absolute pointer-events-none flex items-center justify-center",
      style: {
        bottom: "-34px",
        left: "50%",
        transform: "translateX(-50%)"
      }
    }, /*#__PURE__*/React.createElement("span", {
      className: "relative bg-primary-new text-white text-[10px] font-medium leading-4 tracking-[-0.25px] px-1.5 py-0.5 rounded whitespace-nowrap before:content-[''] before:absolute before:bottom-full before:left-1/2 before:-translate-x-1/2 before:border-l-[4px] before:border-l-transparent before:border-r-[4px] before:border-r-transparent before:border-b-[4px] before:border-b-primary-new before:border-solid"
    }, mark.currentLabel)));
  }), /*#__PURE__*/React.createElement("div", {
    className: twMerge("absolute top-1/2 w-5 h-5 rounded-full border-[3px] border-white border-solid pointer-events-none", disabled ? "bg-gray-400" : "bg-primary-new", !isActive && "transition-[left,box-shadow] duration-300 ease-in-out", thumbShadow),
    style: {
      left: "".concat(displayLeft, "%"),
      transform: "translate(-50%, -50%)"
    }
  })));
};
export default Slider;